/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import clsx from 'clsx'
import type {
  Dispatch,
  KeyboardEventHandler,
  MouseEventHandler,
  MutableRefObject,
  PropsWithChildren,
} from 'react'
import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useReducer,
  useRef,
} from 'react'
import FocusLock from 'react-focus-lock'
import { useIntl } from 'react-intl'
import { RemoveScroll } from 'react-remove-scroll'
import { useElementOverflow } from '../hooks/useElementOverflow'
import { useId } from '../hooks/useId'
import IconButton from '../IconButton/IconButton'
import Close from '../icons/Close'
import Portal from '../Portal/Portal'
import Typography from '../Typography/Typography'
import { ROLE } from '../utilities/aria'
import { Keys } from '../utilities/keyboard'
import nodeToString from '../utilities/nodeToString'

enum ActionTypes {
  AddTitle,
  RemoveTitle,

  AddContent,
  RemoveContent,
}

export enum Backdrops {
  Dark,
  Light,
}

export enum Constraints {
  Default,
  Screen,
  Padded,
}

type Constraint = {
  [key in Constraints]: string
}

const constraints: Constraint = {
  [Constraints.Default]: 'max-w-xl max-h-(screen-16) m-6',
  [Constraints.Screen]: 'max-w-screen max-h-screen',
  [Constraints.Padded]: 'max-w-(screen-16) max-h-(screen-16) m-6',
}

export interface DialogProps {
  /**
   * The backdrop style for the dialog overlay: Dark=0 (dark overlay), Light=1 (white overlay)
   * @default Backdrops.Dark
   */
  backdrop?: Backdrops
  /**
   * The size constraint applied to the dialog: Default=0 (max width 36rem), Screen=1 (full-screen), Padded=2 (full-screen with 16rem margin all around)
   * @default Constraints.Default
   */
  constraint?: Constraints
  /**
   * Whether the dialog can be dismissed by clicking the close button or the backdrop or pressing Escape.
   * When true, a close button will also be displayed in the dialog header
   * @default true
   */
  dismissable?: boolean
  /**
   * Callback function invoked when the dialog is dismissed.
   * Only applicable if `dismissable` is true (default value)
   */
  onDismiss?: () => void
  /**
   * Whether the dialog is currently open/visible
   * @default false
   */
  open?: boolean
}

type DialogReducers = {
  [ActionType in ActionTypes]: (
    state: DialogState,
    action: ReducerActions,
  ) => DialogState
}

interface DialogState {
  dismissable: boolean
  hasContent: boolean
  hasTitle: boolean
  id: string
  parentRef: MutableRefObject<HTMLDivElement | null>
}

type ReducerActions =
  | { type: ActionTypes.AddTitle }
  | { type: ActionTypes.RemoveTitle }
  | { type: ActionTypes.AddContent }
  | { type: ActionTypes.RemoveContent }

type UseDialogValues = [
  state: DialogState,
  dispatch: Dispatch<ReducerActions>,
  onDismiss?: () => void,
]

const DialogContext = createContext<UseDialogValues | null>(null)

const reducers: DialogReducers = {
  [ActionTypes.AddTitle]: (state) => ({ ...state, hasTitle: true }),
  [ActionTypes.RemoveTitle]: (state) => ({ ...state, hasTitle: false }),
  [ActionTypes.AddContent]: (state) => ({ ...state, hasContent: true }),
  [ActionTypes.RemoveContent]: (state) => ({ ...state, hasContent: false }),
}

const runReducer = (state: DialogState, action: ReducerActions): DialogState =>
  reducers[action.type](state, action)

const useDialog = (): UseDialogValues =>
  useContext(DialogContext) as UseDialogValues

function Actions({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return (
    <div className="flex items-center justify-end shrink-0 p-6 space-x-2">
      {children}
    </div>
  )
}

function Content({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null)
  const overflowing = useElementOverflow(ref)
  const [state, dispatch] = useDialog()
  const id = `coconut-dialog-content-${state.id}`

  useLayoutEffect(() => {
    dispatch({ type: ActionTypes.AddContent })

    return () => {
      if (state.parentRef.current) {
        dispatch({ type: ActionTypes.RemoveContent })
      }
    }
  }, [dispatch, state.parentRef])

  return (
    <div
      className={clsx(
        'px-6 overflow-hidden overflow-y-auto focus-visible:ring',
        overflowing ? 'border-b border-t border-black-10' : null,
        state.hasTitle ? 'py-0' : 'pt-6',
      )}
      id={id}
      ref={ref}
    >
      {children}
    </div>
  )
}

function Dialog({
  backdrop = Backdrops.Dark,
  children,
  constraint = Constraints.Default,
  dismissable = true,
  onDismiss,
  open = false,
}: PropsWithChildren<DialogProps>): JSX.Element | null {
  const mouseDownTarget = useRef<EventTarget | null>(null)
  const parentRef = useRef<HTMLDivElement | null>(null)
  const [state, dispatch] = useReducer(runReducer, {
    dismissable,
    hasContent: false,
    hasTitle: false,
    id: useId(),
    parentRef,
  } as DialogState)

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (state.dismissable && mouseDownTarget.current === event.currentTarget) {
      event.stopPropagation()
      onDismiss?.()
    }
  }

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (state.dismissable && event.key === Keys.Escape) {
      event.stopPropagation()
      onDismiss?.()
    }
  }

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = (event) => {
    mouseDownTarget.current = event.target
  }

  // Optimization - ReactFocusLock runs very slowly in test environments.
  // We set this explicitly in Dialog.tests, but consumers can
  // set it explicitly or fallback to true in 'test' states.
  const disabled = global.process
    ? Object.hasOwnProperty.call(
        global.process.env ?? {},
        'DISABLE_SCROLL_FOCUS_LOCK',
      )
      ? global.process.env.DISABLE_SCROLL_FOCUS_LOCK === 'true'
      : global.process.env.NODE_ENV === 'test'
    : false

  return open ? (
    <Portal open={open}>
      <div
        // TODO make aria-describedby configurable so SR users don't have all contents
        // read out for large amounts of content.
        // https://www.pivotaltracker.com/story/show/188341662
        aria-describedby={
          state.hasContent ? `coconut-dialog-content-${state.id}` : undefined
        }
        aria-labelledby={
          state.hasTitle ? `coconut-dialog-header-${state.id}` : undefined
        }
        aria-modal="true"
        className={clsx(
          'fixed inset-0 flex items-center justify-center opacity-100 backdrop-blur-2 z-9998',
          backdrop === Backdrops.Dark ? 'bg-black-45' : 'bg-white-45',
        )}
        /*
         * We can safely ignore the `no-static-element-interactions` warning here
         * because our overlay is only designed to capture any outside
         * clicks, not serve as a clickable element itself.
         *
         * https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-static-element-interactions.md#case-the-event-handler-is-only-being-used-to-capture-bubbled-events
         */
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseDown={handleMouseDown}
        ref={parentRef}
        role={ROLE.Dialog}
      >
        <RemoveScroll enabled={!disabled}>
          <FocusLock disabled={disabled} returnFocus>
            <div
              className={clsx(
                'relative flex flex-col overflow-auto font-sans rounded outline-none shadow-24 bg-white-100',
                constraints[constraint],
              )}
            >
              <DialogContext.Provider value={[state, dispatch, onDismiss]}>
                {children}
              </DialogContext.Provider>
            </div>
          </FocusLock>
        </RemoveScroll>
      </div>
    </Portal>
  ) : null
}

function Header({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  const [state, dispatch, onDismiss] = useDialog()
  const intl = useIntl()
  const id = `coconut-dialog-header-${state.id}`

  useLayoutEffect(() => {
    dispatch({ type: ActionTypes.AddTitle })

    return () => {
      if (state.parentRef.current) {
        dispatch({ type: ActionTypes.RemoveTitle })
      }
    }
  }, [dispatch, state.parentRef])

  return (
    <div
      className={clsx(
        'flex items-center justify-between',
        state.dismissable ? 'px-6 py-4' : 'p-6',
      )}
    >
      <h2 className="flex truncate" id={id} title={nodeToString(children)}>
        <Typography truncate variant="h5">
          {children}
        </Typography>
      </h2>
      {state.dismissable ? (
        <div className="ml-2">
          <IconButton
            aria-label={intl.formatMessage({ id: 'Ui.close' })}
            onClick={onDismiss}
            size="medium"
          >
            <Close />
          </IconButton>
        </div>
      ) : null}
    </div>
  )
}

Dialog.Actions = Actions
Dialog.Content = Content
Dialog.Header = Header

export default Dialog
